import {
  CareActionType,
  CareItem,
  CareItemID,
  FirstAidItem,
  FoodItem,
  SnackItem,
  ToyItem,
} from 'src/replicant/care/care.ruleset';
import { PetAttribute, PetState, PetStatusEffect } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import StateObserver from 'src/StateObserver';
import {
  trackingCareActionFinishEvent,
  trackPinchConversion,
} from 'src/lib/analytics/events';
import { GrantGemsReturn } from 'src/replicant/visit/visit.actions';
import { careActions } from './care.redux';
import {
  delay,
  animDuration,
  getHeader,
  getSceneManager,
  waitForAssets,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import { getFriendsPet, getPet } from '../pet';
import { petIdleSequence } from '../pet/pet.sequence';
import * as Care from 'src/replicant/care';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Visit from 'src/replicant/visit';
import {
  setCompensation,
  setEffectiveMultiplier,
} from '../inventory/inventory.redux';
import { Feature } from 'src/replicant/analytics';
import {
  careActionAnimations,
  getCareActionType,
  getCareItemPosition,
  getFriendPetCarePosition,
  getPetCarePosition,
} from './care.config';
import { duration } from 'src/replicant/utils/duration';
import { getMutex } from 'src/replicant/utils/mutex';
import { Position } from '../position/PositionModel';
import { CareItemController } from './CareItemController';
import { EntityManager } from '../logic/EntityManager';
import { getCareItemAmount } from 'src/replicant/care/care.getters';
import { assertNever, clamp } from 'src/replicant/utils';
import { PetController } from '../pet/PetController';
import { Attributes } from '../pet/pet.redux';
import { careAssets } from 'src/loadingGroups';
import { AssetGroup } from '@play-co/timestep-core';
import * as Fate from 'src/replicant/fate';
import { getTutorialController } from '../tutorial/TutorialController';
import {
  puzzleCoinsPostToyCareSequence,
  puzzleCoinsShouldRunPostToyCareSequence,
} from '../puzzlecoins/puzzlecoins.sequence';
import features from 'src/replicant/ruleset/features';

import { SceneID } from 'src/state/ui';
import { canPerformCareAction } from 'src/replicant/care/care.getters';
import * as Inventory from 'src/replicant/inventory';
import { TutorialHighlightService } from 'src/game/tutorial/services/TutorialHighlightService';

const mutex = getMutex();

/**
 * Executed purchase care item
 */
export async function purchaseCareItemSequence(careItemId: CareItemID) {
  const careItem = Care.ruleset.careItems[careItemId];
  const { type } = careItem;
  const previousVisibleState = Fate.getters.getVisiblePetState(
    StateObserver.getState().user,
    StateObserver.now(),
  );
  const previousFriendVisibleState = getFriendsPet()?.state;

  if (type === 'invitation') {
    throw new Error('Invitation is not supported');
  }

  // Stop attributes auto sync for time of care action
  getPet()?.appearance.attributesAutoSync(false);

  try {
    // Resolve status effect and consume price for action
    const purchase = await StateObserver.invoke.purchaseCareItem(careItemId);

    // Was not enough coins or actions was not allowed
    // TODO: Should we throw error in this case?
    if (!purchase.consumed) return;

    // Update effective multiplier.
    StateObserver.dispatch(
      setEffectiveMultiplier(purchase.effectiveMultiplier),
    );

    StateObserver.dispatch(setCompensation(purchase.refund));

    // Update pet appearance
    await onCareSuccess(
      careItem,
      purchase,
      previousVisibleState,
      previousFriendVisibleState,
    );
  } finally {
    // Resume attributes auto sync after all care animation finished
    getPet()?.appearance.attributesAutoSync(true);
  }

  // This should run only after the first toy care action is completed
  if (puzzleCoinsShouldRunPostToyCareSequence(careItemId)) {
    puzzleCoinsPostToyCareSequence();
  }
}

// ---------------------------------------------------------------------------
// Silent care item purchase (janken care gate)
// ---------------------------------------------------------------------------

export type PurchaseCareItemSilentOpts = {
  highlightMeters?: ('stomach' | 'love')[];
};

export type PurchaseCareItemSilentResult = {
  consumed: boolean;
  notEnoughCoins: boolean;
};

/**
 * Purchase a care item without animations, pet reactions, or puzzle redirects.
 *
 * Used by the janken care gate where the popup stays open and the purchase
 * must happen silently in the background. The caller is responsible for any
 * UI feedback (e.g. "not enough coins" modal).
 *
 * Flow:
 *   1. Pre-check: is the care action allowed? → { consumed: false, notEnoughCoins: false }
 *   2. Pre-check: inventory item available (free)? → skip coin check
 *   3. Pre-check: enough coins? → { consumed: false, notEnoughCoins: true }
 *   4. Freeze meter auto-sync + show highlight overlay (if highlightMeters provided)
 *   5. Invoke purchaseCareItem action (state changes but meters stay frozen)
 *   6. Dispatch effective multiplier + compensation to redux
 *   7. Tween meters smoothly to new values (1.5s) → hide highlight
 *   8. Resume meter auto-sync
 *   9. Return { consumed: true, notEnoughCoins: false }
 */
export async function purchaseCareItemSilent(
  careItemId: CareItemID,
  opts?: PurchaseCareItemSilentOpts,
): Promise<PurchaseCareItemSilentResult> {
  const careItem = Care.ruleset.careItems[careItemId];

  if (careItem.type === 'invitation') {
    throw new Error('Invitation is not supported');
  }

  // Pre-check: is the action allowed at all?
  const state = StateObserver.getState().user;
  const now = StateObserver.now();
  if (!canPerformCareAction(state, careItem.type, now)) {
    return { consumed: false, notEnoughCoins: false };
  }

  // Pre-check: does the user have inventory items (free) or enough coins?
  const inventoryCount =
    Inventory.getters.getCareItems(state)[careItemId]?.count ?? 0;
  if (
    inventoryCount <= 0 &&
    Inventory.getters.getPoints(state) < careItem.price
  ) {
    return { consumed: false, notEnoughCoins: true };
  }

  // Freeze meter auto-sync so we can tween them smoothly
  const pet = getPet();
  pet?.appearance.attributesAutoSync(false);

  // Show meter highlight before state mutation so user sees values change
  const meters = opts?.highlightMeters ?? [];
  let highlightService: TutorialHighlightService | null = null;
  if (meters.length > 0) {
    getHeader()?.registerMeterHandTargets();
    highlightService = new TutorialHighlightService();
    for (const meter of meters) {
      const tag =
        meter === 'stomach' ? 'headerStomachMeter' : 'headerLoveMeter';
      highlightService.addTarget({ tag });
    }
    await highlightService.show();
  }

  try {
    // Invoke action (state changes but meters stay frozen)
    const purchase = await StateObserver.invoke.purchaseCareItem(careItemId);

    if (!purchase.consumed) {
      return { consumed: false, notEnoughCoins: false };
    }

    // Update effective multiplier + compensation in redux (needed for tween calc)
    StateObserver.dispatch(
      setEffectiveMultiplier(purchase.effectiveMultiplier),
    );
    StateObserver.dispatch(setCompensation(purchase.refund));

    // Smoothly tween meters from frozen values to new values
    const header = getHeader();
    if (header && pet) {
      const targetAttributes = getAttributesAfterCareItem(pet, careItem);
      await header.tweenAttributes(pet, targetAttributes, 1500);
      pet.appearance.setAttributes(targetAttributes);
    }

    return { consumed: true, notEnoughCoins: false };
  } finally {
    pet?.appearance.attributesAutoSync(true);
    await highlightService?.hide();
  }
}

/**
 * Executed care action
 */
export async function performCareActionSequence(careAction: CareActionType) {
  const previousVisibleState = Fate.getters.getVisiblePetState(
    StateObserver.getState().user,
    StateObserver.now(),
  );

  // Resolve status effect and consume price for action
  const purchase = await StateObserver.invoke.performCareAction(careAction);

  // Was not enough coins or actions was not allowed
  // TODO: Should we throw error in this case?
  if (!purchase.consumed) return;

  // Update pet appearance
  await onCareSuccess({ type: careAction }, purchase, previousVisibleState);
}

/**
 * Update friend pet view
 */
async function updateFriendPetStats(updatePayload: GrantGemsReturn) {
  const state = StateObserver.getState();
  const { visitSession } = state.user.visit;

  if (!visitSession) {
    return;
  }

  const friendsPet = getFriendsPet();

  if (!friendsPet) {
    return;
  }

  const { appearance } = friendsPet;

  if (!updatePayload) return;

  const { attributes, statusEffects } = updatePayload;
  appearance.setAttributes(attributes);
  appearance.setStatusEffects(statusEffects);
}

export async function consumeCareItem(
  item: FoodItem | SnackItem | FirstAidItem,
) {
  const pet = getPet();
  const friendPet = getFriendsPet();
  const type = getCareActionType(item);
  const isVisit = !!friendPet;

  const careItems: CareItemController[] = [];
  await Promise.all([
    // Move pets into position
    pet.moveTo(getPetCarePosition(type, !!friendPet)),
    friendPet?.moveTo(getFriendPetCarePosition(type)),

    // Create care items and add to scene
    ...[pet, friendPet].map(async (ctrl) => {
      if (!ctrl) return;

      const entity = EntityManager.createOrGetCareItem(item.id, ctrl.id);

      const careItem = new CareItemController(getSceneManager(), entity);
      careItems.push(careItem);

      const position = getCareItemPosition(item, isVisit, ctrl === friendPet);
      await careItem.position.moveTo(position, 0);
    }),
  ]);

  // Play care item animation
  tweenAttributes(item);
  await Promise.all(careItems.map(({ appearance }) => appearance.animate()));
  await delay(animDuration);

  // If action involves a care item, wait for UI to update, and clear the item.
  careItems.forEach((item) => item.destroy());

  pet.appearance.setAction(undefined);
  friendPet?.appearance.setAction(undefined);

  // Wait one more second if not visiting a friend.
  if (!isVisit) {
    await waitForItPromise(duration({ seconds: 1 }));
  }

  await postCareActionSequence();
}

async function useToyItem(item: ToyItem) {
  const pet = getPet();
  const friendPet = getFriendsPet();
  const type = getCareActionType(item);
  const isVisit = !!friendPet;

  // Create item.
  const careItem = new CareItemController(
    getSceneManager(),
    EntityManager.createOrGetCareItem(item.id),
  );

  // Await until everything is in position.
  await Promise.all([
    // Move all pets to the care action position.
    pet.moveTo(getPetCarePosition(type, isVisit)),
    friendPet?.moveTo(getFriendPetCarePosition(type)),

    // Add care item to scene
    careItem.position.moveTo(getCareItemPosition(item, isVisit), 0),
  ]);

  // Play care item animation
  tweenAttributes(item);
  await careItem.appearance.animate();
  await delay(animDuration);

  // If action involves a care item, wait for UI to update, and clear the item.
  careItem.destroy();
  pet.appearance.setAction(undefined);
  friendPet?.appearance.setAction(undefined);

  // Wait one more second if not visiting a friend.
  if (!isVisit) {
    await delay(duration({ seconds: 1 }));
  }

  await postCareActionSequence();
}

function tweenAttributes(
  item: Exclude<CareItem, { type: 'invitation' }>,
  duration = 3000,
) {
  const pet = getPet();
  const friendPet = getFriendsPet();
  const type = getCareActionType(item);

  const header = getHeader();
  const petAttributes = getAttributesAfterCareItem(pet, item);
  pet.appearance.setAction(careActionAnimations[type]);
  pet.appearance.setFlipped(!!friendPet);
  header?.tweenAttributes(pet, petAttributes, duration).then(() => {
    pet.appearance.setAttributes(petAttributes);
  });

  if (friendPet) {
    const friendAttributes = getAttributesAfterCareItem(friendPet, item);
    const friendAppearance = friendPet.appearance;
    friendAppearance.setAction(careActionAnimations[type]);
    friendAppearance.setFlipped(false);
    header?.tweenAttributes(friendPet, friendAttributes, duration).then(() => {
      friendAppearance.setAttributes(friendAttributes);
    });
  }
}

// Calculates the pet's attributes after the care item has been applied.
function getAttributesAfterCareItem(
  { appearance }: PetController,
  careItem: CareItem,
): Attributes {
  const attributes = { ...appearance.getAttributes() };
  Pet.ruleset.petAttributes.forEach((attribute) => {
    const currentAttribute = attributes[attribute];
    attributes[attribute] = getAttributeValueAfterCareItem(
      careItem,
      attribute,
      currentAttribute,
    );
  });
  return attributes;
}

// Calculates the value of an attribute after a care item has been applied.
function getAttributeValueAfterCareItem(
  careItem: CareItem,
  attribute: PetAttribute,
  initialValue: number,
): number {
  const multiplier =
    attribute === 'love' || attribute === 'stomach'
      ? StateObserver.getState().inventory.effectiveMultiplier[attribute]
      : 1;

  const careItemAmount =
    (getCareItemAmount(careItem, attribute) ?? 0) * multiplier;
  if (!careItemAmount) {
    return initialValue;
  }

  const { decayMultipliers, attributeBounds } = Pet.ruleset;
  const addedValue = -decayMultipliers[attribute] * careItemAmount;
  // Clamp attribute values to the bounds
  return clamp(
    initialValue + addedValue,
    attributeBounds.min,
    attributeBounds.max,
  );
}

async function handleHygieneAction(type: 'toilet' | 'bath') {
  const pet = getPet();
  const friendPet = getFriendsPet();
  const visitSession = !!friendPet;

  // Move all pets to the care action position.
  await Promise.all([
    pet.moveTo(getPetCarePosition(type, visitSession)),
    friendPet?.moveTo(getFriendPetCarePosition(type)),
  ]);

  pet.appearance.setFlipped(false);

  // Start care animation.
  pet.appearance.setAction(careActionAnimations[type]);
  friendPet?.appearance.setAction(undefined);

  // Normal care actions take 2s, while visiting the time is shortened to 1.5s.
  const delay = duration({ seconds: visitSession ? 1.5 : 2 });

  await waitForItPromise(delay);

  // If action requires swapping pets, do it.
  if (visitSession) {
    // Start by hanging the pet.
    pet.appearance.setAction('hang');
    friendPet?.appearance.setAction('hang');

    await waitForItPromise(500);

    // Swap positions.
    await Promise.all([
      pet.moveTo(getFriendPetCarePosition(type)),
      friendPet?.moveTo(getPetCarePosition(type, visitSession)),
    ]);

    friendPet?.appearance.setFlipped(false);

    // Play animation.
    pet.appearance.setAction(undefined);
    friendPet?.appearance.setAction(careActionAnimations[type]);

    // Await for animation to finish.
    await waitForItPromise(delay);
  }

  // Wait one more second if not visiting a friend.
  if (!visitSession) {
    await waitForItPromise(duration({ seconds: 1 }));
  }

  await postCareActionSequence();
}

/**
 * Run care item pet status update
 */
async function onCareSuccess(
  care:
    | ToyItem
    | SnackItem
    | FirstAidItem
    | FoodItem
    | { id?: undefined; type: CareActionType },
  purchase: {
    consumed: boolean;
    targetStatusEffects?: PetStatusEffect[];
    resolvedStatusEffect?: boolean;
    effectiveMultiplier?: { stomach: number; love: number };
    bPointsConsumed: number;
  },
  previousVisibleState: PetState,
  previousFriendVisibleState?: PetState,
) {
  await mutex.lock();
  try {
    const {
      pet,
      level,
      user: {
        visit: { visitSession },
      },
    } = StateObserver.getState();
    const friendId = visitSession?.friendId;
    const friendAttributes = friendId ? pet[friendId]?.attributes : undefined;
    const friendAppearance = friendId ? pet[friendId] : undefined;
    const friendLevel = friendId ? level[friendId] : undefined;
    const { getVisitedCount, getVisitsCount } = Visit.getters;
    const { targetStatusEffects, resolvedStatusEffect } = purchase;
    const { setCareAction } = careActions;
    const { hasTriedToCurePet, getResultOfCureAction } = Care.getters;

    // Make sure item tried to cure visible state.
    const triedCuringVisibleState = hasTriedToCurePet(
      targetStatusEffects ?? [],
      previousVisibleState,
    );

    const { id: careItemId, type } = care;
    const careActionsCount = purchase.effectiveMultiplier
      ? Math.max(...Object.values(purchase.effectiveMultiplier))
      : 1;
    for (let i = 0; i < careActionsCount; i++) {
      trackingCareActionFinishEvent({
        action_type: care.type,
        item_id: care.id ?? 'none',
        result: getResultOfCureAction(
          triedCuringVisibleState,
          !!resolvedStatusEffect,
        ),
        withFriends: !!friendId,
        friendID: friendId,
        friendPetID: friendAppearance?.species,
        friendPetStomach: friendAttributes?.stomach,
        friendPetLove: friendAttributes?.love,
        friendDirtiness: friendAttributes?.dirtiness,
        friendUrine: friendAttributes?.urine,
        friendStress:
          friendAttributes && Pet.ruleset.getStressValue(friendAttributes),
        friendToothAcidity: friendAttributes?.toothAcidity,
        friendAge: friendAttributes?.age,
        friendPlayerLevel: friendLevel?.current,
        visits: friendId
          ? getVisitsCount(StateObserver.getState().user, friendId)
          : undefined,
        visited: friendId
          ? getVisitedCount(StateObserver.getState().user, friendId)
          : undefined,
        bPointsConsumed: purchase.bPointsConsumed,
      });
    }

    // Sync friend pet state if visiting a friend.
    // Do this as soon as possible since we can be kicked out of the friend's room while waiting for the animation to finish.
    const updatePayload = friendId
      ? await StateObserver.invoke.syncFriendState({
          attributes: pet[friendId].attributes,
          statusEffects: pet[friendId].statusEffects,
        })
      : undefined;

    StateObserver.dispatch(
      setCareAction({
        // TODO: fix first_aid and firstAid inconsistency
        type: type === 'first_aid' ? 'firstAid' : type,
        item: careItemId,
      }),
    );

    // Wait for care item animation to be loaded.
    if (type in careAssets) {
      await waitForAssets(
        new AssetGroup([careAssets[type as keyof typeof careAssets]]),
        {
          feature: Feature.CARE._,
          subFeature: Feature.CARE.ASSETS,
        },
      );
    }

    const previousOffset = EntityManager.getCurrentEntitiesOffset();

    switch (care.type) {
      // Hygiene actions. Will change scene and be executed one pet at a time.
      case 'bath':
      case 'toilet':
        EntityManager.setEntitiesOffset({ offsetX: 0, offsetY: 0 });
        await handleHygieneAction(care.type);
        break;

      // Care actions that involve items.
      case 'food':
      case 'snack':
      // eslint-disable-next-line no-fallthrough
      case 'first_aid':
        await consumeCareItem(care);
        break;

      // Special case of items. Since only one item is instantiated when visiting a friend.
      case 'toy':
        await useToyItem(care);
        break;

      default:
        assertNever(care);
    }

    EntityManager.setEntitiesOffset(previousOffset);

    // Use state after action is applied.
    const now = StateObserver.now();
    const { user } = StateObserver.getState();

    const { getVisiblePetState } = Fate.getters;
    const currentVisibleState = getVisiblePetState(user, now);

    // Show dialog if item tried to cure visible state.
    if (triedCuringVisibleState) {
      const visibleStateChanged = currentVisibleState !== previousVisibleState;

      const pet = getPet();

      if (pet.species) {
        await pet.conversation.begin(
          visibleStateChanged ? 'cured' : 'not_cured',
          pet.species,
          previousVisibleState,
        );
      }
    }

    // If update payload is defined, update friend pet attributes.
    if (updatePayload) {
      // Use return from grant gems to update attributes.
      await updateFriendPetStats(updatePayload);

      // Check if it should show dialog for friend pet.
      const friendsPet = getFriendsPet();
      if (previousFriendVisibleState && friendsPet) {
        const triedCuringFriendVisibleState = hasTriedToCurePet(
          targetStatusEffects ?? [],
          previousFriendVisibleState,
        );

        // Show dialog if item tried to cure visible state.
        if (triedCuringFriendVisibleState) {
          // Move pet away so they don't overlap.
          if (triedCuringVisibleState) {
            await getPet().moveTo({
              scene: 'friendsLivingRoom',
              location: 'lm',
            });
          }

          // Show dialog.
          const visibleStateChanged =
            friendsPet.state !== previousFriendVisibleState;

          if (friendsPet.species) {
            await friendsPet.conversation.begin(
              visibleStateChanged ? 'cured' : 'not_cured',
              friendsPet.species,
              previousFriendVisibleState,
            );
          }
        }
      }

      // Show gems reward popup if gems were granted.
      if (updatePayload.granted) {
        await friendsPet?.startConversation('friend_appreciation');

        await openPopupPromise('popupVisitReward', {
          reward: Visit.ruleset.gemsReward,
        });

        await StateObserver.invoke.claimVisitGems();

        trackPinchConversion({
          action: 'social',
          feature: Feature.VISIT._,
          subFeature: Feature.VISIT.REWARD,
          resource: 'gems',
        });
      }
    }

    StateObserver.dispatch(careActions.setCareAction(null));
  } finally {
    mutex.release();
  }

  await getTutorialController().handleEvent('careActionCompleted');

  // Start pet idle sequence.
  await petIdleSequence();
}

async function postCareActionSequence() {
  const pet = getPet();
  const friendPet = getFriendsPet();
  const position: Position = friendPet
    ? {
        scene: 'friendsLivingRoom',
        location: 'lm',
      }
    : {
        scene: getSceneManager().baseSceneId,
        location: 'c',
      };

  await Promise.all([
    pet.moveTo(position),
    friendPet?.moveTo({
      scene: 'friendsLivingRoom',
      location: 'rm',
    }),
  ]);

  // sync game attributes state with player state after care action finished
  const { user } = StateObserver.getState();
  const attributes = Pet.getters.getAttributeValues(user, StateObserver.now());

  pet.appearance.setAttributes(attributes);
}

/**
 * Purchase growth capsule and trigger pet idle sequence so the evolution dialog is shown.
 */
export async function purchaseGrowthCapsuleSequence() {
  await StateObserver.invoke.purchaseGrowthCapsule();
  await petIdleSequence();
}
